import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { exportOptions, tablesConfig, tables } from '../../constants';
import ToggleButton from './ToggleButtonV2';
import Label from './Label';
import {
  usePagination,
  useExportData,
  usePolling,
  useFetchPaginatedData,
  useFetchDataOnFilterChange,
  useFirstRender,
} from '../../hooks';
import { FacilityUnitContext } from '../../context';
import PaginatorV2 from './PaginatorV2';
import DataVisualizationContainer from './DataVisualizationContainer';
import TableSkeleton from './TableSkeleton';
import PaginationSkeleton from './PaginationSkeleton';
import {
  normalizeEventsForLocationHistoryV2,
  normalizeEventsForUsageHistory,
  normalizeEventsForWaterChanges,
  normalizeEventsForBatteryLife,
} from '../../utils/normalizeEvents';
import getDateRange from '../../utils/dates';
import _ from 'lodash';

/**
 * This component is responsible for displaying data visualizations for a specific data type.
 * It includes:
 * - A table to display the data records.
 * - Pagination controls for navigating between pages of data.
 * - Loading and error handling states to improve user experience.
 *
 * Features:
 * - Redirects the user to the unit selection page if no unit is selected.
 * - Allows exporting data using the `handleExport` function (which displays a toast on completion).
 * - Displays a loading indicator or an error message based on the current state.
 * - Dynamically renders children components to display the data.
 *
 * @param {Object} dataType the type of data to display
 * @param {ReactNode} children The table or visualization content to render
 * @param {ReactNode} [filterComponent=null] - An optional component (e.g. a filter button).
 * @param {Object} [filterParams={}] - Additional parameters for fetching/filtering data (e.g., { event_id: '123' }).
 * @param {Boolean} [areFiltersActive=false] - Whether filters are currently active.
 * @returns {JSX.Element}
 */
const DataVisualizationView = ({
  dataType,
  children,
  filterComponent = null,
  filterParams = {},
  areFiltersActive = false,
  hideExtraFilters = false,
}) => {
  const [newCurrentPage, setNewCurrentPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isShowAllActive, setIsShowAllActive] = useState(true);
  const [showEventsWithTransit, setShowEventsWithTransit] = useState(true);
  const { selectedUnit } = useContext(FacilityUnitContext);
  const navigate = useNavigate();
  const recordsPerPage = 10;
  const configTable = tablesConfig[dataType];
  const firstRender = useFirstRender();
  const { startDate, endDate } = getDateRange(selectedDate);

  const filterParamsMemo = useMemo(() => {
    return {
      event_id: filterParams.event_id,
      show_transit: dataType === 'usage' ? showEventsWithTransit : null,
      ...(filterParams?.metric_id && { metric_id: filterParams.metric_id }),
      ...(startDate && { start_date: startDate, end_date: endDate }),
    };
  }, [
    filterParams.event_id,
    filterParams.metric_id,
    showEventsWithTransit,
    startDate,
    endDate,
    dataType,
  ]);

  const paginatedDataHook = useFetchPaginatedData(
    configTable.fetchFunction,
    filterParamsMemo,
    recordsPerPage,
    newCurrentPage,
    configTable.fetchFunctionWithFilters,
    dataType,
  );

  const { data, loading, error, fetchData, totalItems, fetchDataWithFilters } = paginatedDataHook;

  useFetchDataOnFilterChange(filterParamsMemo, fetchData, fetchDataWithFilters);

  useEffect(() => {
    fetchData(true);
  }, [newCurrentPage, fetchData, startDate]);

  const selectedCallback = areFiltersActive ? fetchDataWithFilters : fetchData;

  usePolling(selectedCallback, 15000);

  const rawData = _.isArray(data) ? data : [];

  let normalizedData;
  if (dataType === tables.location) {
    normalizedData = normalizeEventsForLocationHistoryV2(rawData);
  } else if (dataType === tables.usage) {
    normalizedData = normalizeEventsForUsageHistory(rawData);
  } else if (dataType === tables.waterChanges) {
    normalizedData = normalizeEventsForWaterChanges(rawData);
  } else if (dataType === tables.battery) {
    normalizedData = normalizeEventsForBatteryLife(rawData);
  } else {
    normalizedData = rawData;
  }

  const handleExport = useExportData(normalizedData, configTable.columns, configTable.title);

  const onClickShowAllSessions = () => {
    setSelectedDate(null);
    setIsShowAllActive(true);
  };

  const {
    currentPage,
    currentRecords,
    totalPages,
    getPaginatedPageNumbers,
    handlePageChange,
    goToPrevPage,
    goToNextPage,
    resetPagination,
  } = usePagination(normalizedData, recordsPerPage, totalItems, selectedDate);

  const memoizedFilterParams = useMemo(() => filterParamsMemo, [filterParamsMemo]);

  useEffect(() => {
    if (firstRender) return;
    if (memoizedFilterParams?.event_id) {
      resetPagination();
    }
  }, [memoizedFilterParams, firstRender, resetPagination]);

  const onClose = () => navigate('/unit');

  useEffect(() => {
    setNewCurrentPage(currentPage);
  }, [currentPage]);

  useEffect(() => {
    if (!selectedUnit) {
      navigate('/facility-selection'); // Redirect if no unit is selected
      return;
    }
  }, [selectedUnit, navigate]);

  const onDateChange = (date) => {
    setSelectedDate(date);
    setIsShowAllActive(false);
    setNewCurrentPage(1);
  };

  // Loader should be shown only if there is data available in the table.
  // This way we avoid to disrupt user interaction when updating table
  const tableIsEmpty = !Boolean(currentRecords.length);
  const showSkeleton = tableIsEmpty && loading;
  const showTable = !showSkeleton && !error;
  const showTransitToggle = dataType === 'usage';

  const renderPaginator = () => (
    <>
      {!tableIsEmpty && (
        <div className="mt-auto mb-2">
          <PaginatorV2
            getPaginatedPageNumbers={getPaginatedPageNumbers}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            total={totalPages}
            goToPrevPage={goToPrevPage}
            goToNextPage={goToNextPage}
          />
        </div>
      )}
    </>
  );

  const renderPaginationSkeleton = () => (
    <div className="mt-auto mb-2">
      <PaginationSkeleton />
    </div>
  );

  const handleShowTransit = () => {
    setShowEventsWithTransit((prev) => !prev);
  };

  return (
    <>
      <DataVisualizationContainer
        hideExtraFilters={hideExtraFilters}
        title={configTable?.title}
        iconType={tables[dataType]}
        onClose={onClose}
        onRefresh={fetchData}
        onExport={handleExport}
        exportOptions={exportOptions}
        selectedDate={selectedDate}
        onDateChange={onDateChange}
        onShowAllSessions={onClickShowAllSessions}
        isShowAllActive={isShowAllActive}
        filterComponent={filterComponent}
        selectedUnit={selectedUnit}
      >
        <div className="flex flex-col flex-grow">
          {showTransitToggle && (
            <div className="toggle-container flex sm:justify-end">
              <Label variant="gray">Show Transits</Label>
              <ToggleButton status={showEventsWithTransit} onToggle={handleShowTransit} />
            </div>
          )}
          {/** TODO implement loading status once its design is defined */}
          {showSkeleton && (
            <>
              <div className="mb-1 -mt-6">{renderPaginationSkeleton()}</div>
              <div className="flex-grow overflow-auto">
                <TableSkeleton columns={configTable.columns} />
              </div>
              {renderPaginationSkeleton()}
            </>
          )}
          {/** TODO implement error status once its design is defined */}
          {error && (
            <div className="relative p-8 text-red-500 flex justify-center items-center w-full">
              {error}
            </div>
          )}
          {showTable && (
            <>
              <div className="-mt-2 mb-6">{renderPaginator()}</div>
              <div className="flex-grow overflow-y-auto">
                <div className="overflow-x-auto">
                  {children({ data, currentRecords, columns: configTable.columns })}
                </div>
              </div>
              {renderPaginator()}
            </>
          )}
        </div>
      </DataVisualizationContainer>
    </>
  );
};

export default DataVisualizationView;
